import { createSelector, createSlice } from '@reduxjs/toolkit';

import { SlicePayload, UntypedObject } from '@/types';
import { AddressModel, UserModel } from '@/models';
import type { RootState } from '@/redux/create-store';

type UserStateStatusValue = { success: true, redirectUrl?: string } | { error: any } | Record<string, never>;
type UserStateStatusKey = 'get' | 'update' | 'delete';

type UserStateStatus = {
  showConfirm?: boolean;
} & { [key in UserStateStatusKey]?: UserStateStatusValue };

export type UserState = {
  content?: UserModel;
  settings: UntypedObject;
  status: UserStateStatus;
  passwordChanged: boolean;
  emailChanged: boolean;
  notebook?: UntypedObject;
  showAdminBadges?: boolean;
  hashMD5: string;
  hashSha256: string;
  pickIdIntoSha256: string;
}

export const initialState: UserState = {
  content: undefined,
  settings: {},
  status: {} as any,
  passwordChanged: false,
  emailChanged: false,
  hashMD5: '',
  hashSha256: '',
  pickIdIntoSha256: ''
};

const user = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // USER
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getUser: (state) => {
      buildStatus(state, 'get');
    },

    getUserSuccess: (state, action: SlicePayload<{ user: UserModel }>) => {
      state.content = action.payload.user;
      buildStatus(state, 'get', true);
    },

    getUserError: (state, action: SlicePayload<{ error: any }>) => {
      buildStatus(state, 'get', false, action.payload.error);
    },

    getUserClear: (state) => {
      buildStatus(state, 'get');
    },

    // UPDATE
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateUser: (state, _action: SlicePayload<{ user: UserModel }>) => {
      buildStatus(state, 'update');
    },

    updateUserSuccess: (state, action: SlicePayload<{ user: UserModel }>) => {
      state.content = action.payload.user;
      buildStatus(state, 'update', true);
    },

    updateUserError: (state, action: SlicePayload<{ error }>) => {
      buildStatus(state, 'update', false, action.payload.error);
    },

    updateUserClear: (state) => {
      buildStatus(state, 'update');
    },

    // PASSWORD
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateUserPassword: (state, _action: SlicePayload<{ password: string, oldPassword?: string }>) => {
      buildStatus(state, 'update');
    },

    updateUserPasswordSuccess: (state) => {
      state.passwordChanged = true;
    },

    updateUserPasswordClear: (state) => {
      state.passwordChanged = false;
    },

    // EMAIL
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateUserEmail: (state, _action: SlicePayload<{ content: { password: string, email: string } }>) => {
      buildStatus(state, 'update');
    },

    updateUserEmailSuccess: (state) => {
      state.emailChanged = true;
      buildStatus(state, 'update', true);
    },

    updateUserEmailClear: (state) => {
      state.emailChanged = false;
      buildStatus(state, 'update');
    },

    // UPDATE ADDRESS
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateUserAddress: (state, _action: SlicePayload<{ address: AddressModel }>) => {
      buildStatus(state, 'update');
    },

    updateUserAddressSuccess: (state, action: SlicePayload<{ user: UserModel, idToSelect?: string }>) => {
      state.content = action.payload.user;
      buildStatus(state, 'update', true);
    },

    updateUserAddressError: (state, action: SlicePayload<{ error: any }>) => {
      buildStatus(state, 'update', false, action.payload.error);
    },

    updateUserAddressAndValidateInExproOrder: (state, _action: SlicePayload<{address: AddressModel }>) => {
      buildStatus(state, 'update');
    },

    // DELETE ADDRESS
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    deleteUserAddress: (state, _action: SlicePayload<{ addressId: string }>) => {
      buildStatus(state, 'delete');
    },

    deleteUserAddressSuccess: (state, action: SlicePayload<{ user: UserModel }>) => {
      state.content = action.payload.user;
      buildStatus(state, 'delete', true);
    },

    deleteUserAddressError: (state, action: SlicePayload<{ error: any }>) => {
      buildStatus(state, 'delete', false, action.payload.error);
    },

    deleteUserAddressClear: (state) => {
      buildStatus(state, 'delete');
    },

    // SETTINGS
    setUserSettings: (state, action: SlicePayload<{ content: UntypedObject }>) => {
      state.settings = action.payload.content;
    },

    updateUserSetting: (state, action: SlicePayload<{ name: string, value: any }>) => {
      const { name, value } = action.payload;
      state.settings[name] = value;
    },

    // LOYALTY PROGRAM
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateLoyaltyProgram: (state, action: SlicePayload<{t: (_k: string) => string}>) => {
    },

    // COMMUNICATION
    updateUserCommunicationPreferences: (state, _action: SlicePayload<{ storeId: string, langComm: string }>) => {
      buildStatus(state, 'update');
    },

    encodeUserEmail: (state, action: SlicePayload<{ sha256: string, md5: string }>) => {
      state.hashMD5 = action.payload.md5;
      state.hashSha256 = action.payload.sha256;
    },

    encodeUserPickId: (state, action: SlicePayload<string>) => {
      state.pickIdIntoSha256 = action.payload;
    }
  }
});

export const buildStatus = (state: UserState, name: UserStateStatusKey, success = undefined, error = undefined) => {
  state.status = {
    [name]: { success, error }
  };
};

export const {
  getUser, getUserSuccess, getUserError, getUserClear,
  updateUser, updateUserSuccess, updateUserError, updateUserClear,
  updateUserPassword, updateUserPasswordSuccess, updateUserPasswordClear,
  updateUserEmail, updateUserEmailSuccess, updateUserEmailClear,
  updateUserAddress, updateUserAddressSuccess, updateUserAddressError, updateUserAddressAndValidateInExproOrder,
  deleteUserAddress, deleteUserAddressSuccess, deleteUserAddressError,
  setUserSettings, updateUserSetting,
  updateLoyaltyProgram, encodeUserEmail, encodeUserPickId,
  updateUserCommunicationPreferences
} = user.actions;

export default user.reducer;

const userContent = (state: RootState) => state.user.content;

const userState = (state: RootState) => state.user;

export const selectUserEmail = createSelector(
  [userContent],
  (user) => user?.email
);

export const selectUserNewsLetterSubscriptionStatus = createSelector(
  [userContent],
  (user) => user?.newsletter
);

export const selectUserAddresses = createSelector(
  [userContent],
  (user) => user?.addresses
);

export const selectHashedEmail = createSelector(
  [userState],
  (user) => ({ hashMD5: user.hashMD5, hashSha256: { email: user.hashSha256, pickId: user.pickIdIntoSha256 } })
);
